<script setup lang="ts">
  import videoElementSvg from '@/assets/video-element.svg?raw'
  import { useBlockElements } from '@/composable/useBlockElements'
  import { IElement, runWithRejectTimeout, useHtmlToCanvasFactory } from '@gohighlevel/ghl-proposals-common'
  import Player from '@vimeo/player'
  import postscribe from 'postscribe'
  import { PropType, computed, inject, nextTick, onMounted, ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import CWrapper from './CWrapper.vue'

  const props = defineProps({
    element: {
      type: Object as PropType<IElement>,
      required: true,
    },
  })

  const VIMEO_TIMEOUT_MS = 5000

  /** Fallback when Vimeo thumbnail fetch fails: grey background + video-element icon centered (SVG as-is) */
  const DEFAULT_BLANK_VIDEO_IMAGE = (() => {
    const iconSvg = (videoElementSvg as string).trim()
    const viewBox = '0 0 400 225'
    const iconW = 48
    const iconH = 49
    const x = (400 - iconW) / 2
    const y = (225 - iconH) / 2
    const wrapper =
      '<svg xmlns="http://www.w3.org/2000/svg" width="400" height="225" viewBox="' +
      viewBox +
      '">' +
      '<rect width="400" height="225" fill="#e5e7eb"/>' +
      '<g transform="translate(' +
      x +
      ',' +
      y +
      ')">' +
      iconSvg +
      '</g></svg>'
    return 'data:image/svg+xml,' + encodeURIComponent(wrapper)
  })()

  const { t } = useI18n()
  const isPDFView = inject<boolean>('isPDFView', false)
  const { addBlockElement } = useBlockElements()
  const { getVideoThumbnail, getVideoElementUrl, isVimeoEmbedUrl } = useHtmlToCanvasFactory()

  const thumbnailUrl = ref('')
  const showThumbnail = computed(() => isPDFView && !!thumbnailUrl.value)

  const videoElementDimensions = computed(() => {
    const styles = props.element?.responsiveStyles.large
    return {
      height: styles?.height ? styles?.height + 'px' : '',
      width: styles?.width ? styles?.width + 'px' : '',
    }
  })

  const customStyleForImageParent = computed(() => {
    const styles = props.element?.responsiveStyles.large
    return {
      backgroundColor: styles?.backgroundColor,
      marginTop: styles?.marginTop,
      marginBottom: styles?.marginBottom,
      marginLeft: styles?.marginLeft,
      marginRight: styles?.marginRight,
      paddingTop: styles?.paddingTop,
      paddingBottom: styles?.paddingBottom,
      paddingLeft: styles?.paddingLeft,
      paddingRight: styles?.paddingRight,
      height: videoElementDimensions.value.height,
      maxWidth: videoElementDimensions.value.width,
      overflow: 'auto',
    }
  })

  const videoElementId = computed(() => {
    return props.element.id + '-hl-custom-code'
  })

  async function generateThumbnailFromUrl(videoUrl: string) {
    const iframe = document.createElement('iframe')
    iframe.src = videoUrl
    thumbnailUrl.value = await getVideoThumbnail(iframe)
  }

  /**
   * PDF view + Vimeo only:
   * 1) Render the iframe into the container via postscribe
   * 2) Wait for Vimeo player.ready() with timeout
   * 3) If timeout/error -> remove iframe + generate thumbnail + show it
   */
  async function renderVimeoOrFallbackInPdfView() {
    const html = props.element?.component?.options?.html ?? ''
    const videoUrl = getVideoElementUrl(html)
    if (!videoUrl || !isVimeoEmbedUrl(videoUrl)) return

    // Reset any prior thumbnail so we try iframe first
    thumbnailUrl.value = ''

    // Inject iframe into the same container div (like normal)
    postscribe(`#${videoElementId.value}`, html, {
      done: async () => {
        const container = document.getElementById(videoElementId.value)
        const iframe = container?.querySelector('iframe') as HTMLIFrameElement | null
        if (!iframe) {
          // If iframe didn’t render, fallback immediately
          try {
            await generateThumbnailFromUrl(videoUrl)
          } catch (e) {
            console.error('iframe missing + thumbnail failed', e)
            thumbnailUrl.value = DEFAULT_BLANK_VIDEO_IMAGE
          }
          return
        }

        try {
          const player = new Player(iframe)
          await runWithRejectTimeout(player.ready(), VIMEO_TIMEOUT_MS, 'vimeo-not-ready')
        } catch (e) {
          console.error('vimeo player not ready within timeout', e)
          try {
            iframe.src = 'about:blank'
          } catch {}
          try {
            iframe.remove()
          } catch {}

          // Generate thumbnail
          try {
            await generateThumbnailFromUrl(videoUrl)
          } catch (thumbErr) {
            console.error('thumbnail generation failed', thumbErr)
            thumbnailUrl.value = DEFAULT_BLANK_VIDEO_IMAGE
          }
        }
      },
    })
  }

  const loadVidyardVideo = function () {
    if (props.element?.component?.options?.html.includes('vidyard.com') && (window as any)?.vidyardEmbed) {
      ;(window as any)?.vidyardEmbed?.api.renderDOMPlayers()
    }
  }

  onMounted(() => {
    const html = props.element?.component?.options?.html ?? ''
    const videoUrl = getVideoElementUrl(html)
    const isVimeo = !!videoUrl && isVimeoEmbedUrl(videoUrl)

    if (isPDFView && isVimeo) {
      // PDF + Vimeo only: try iframe first, fallback to thumbnail if player doesn't render
      renderVimeoOrFallbackInPdfView()
    } else {
      // All other cases (non-PDF, or PDF + non-Vimeo): normal flow
      postscribe(`#${videoElementId.value}`, html)
      setTimeout(() => {
        loadVidyardVideo()
      }, 500)
    }

    nextTick(() => {
      addBlockElement(props.element.id, props.element.type)
    })
  })
</script>

<template>
  <CWrapper class="video-el print:break-inside-avoid break-inside-avoid">
    <div :style="customStyleForImageParent">
      <div class="iframe-container">
        <!-- Video block: thumbnail img or iframe container (never wrapped by anchor so iframe loads) -->
        <img v-if="showThumbnail" :src="thumbnailUrl" :style="videoElementDimensions" class="c-video-thumbnail" alt="" />
        <div v-else class="c-video-iframe" :id="videoElementId" :style="videoElementDimensions"></div>
        <!-- PDF view only: overlay anchor (sibling, not wrapping iframe) for Chromium PDF hyperlink -->
        <a
          v-if="isPDFView"
          :href="props.element.component?.options?.src ?? ''"
          target="_blank"
          rel="noopener noreferrer"
          class="c-video-pdf-link"
          :aria-label="t('common.video')"
        />
      </div>
    </div>
  </CWrapper>
</template>

<style lang="scss">
  .video-el {
    .c-video-iframe {
      position: absolute;
      display: block;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: contain;
    }

    .c-video-thumbnail {
      position: absolute;
      display: block;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover; /* fill container like the real player; may crop edges */
    }

    .c-video-iframe iframe {
      position: absolute;
      display: block;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }

    .iframe-container {
      position: relative;
      z-index: 0;
      width: 100%;
      height: 0;
      padding-top: 56.25%;
      overflow: hidden;
    }

    /* Overlay anchor for PDF hyperlink */
    .c-video-pdf-link {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 1;
      display: block;
      text-decoration: none;
      color: inherit;
    }
  }
</style>
